import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { debugLogger } from '@/utils/debugLogger';

type PaymentMethod = 'PIX' | 'CREDIT_CARD' | null;

interface PixQrCodeData {
  id: string;
  encodedImage: string;
  payload: string;
  expirationDate: string;
  description: string;
  isTestMode?: boolean; // Indica se PIX foi gerado em ambiente sandbox
}

interface PaymentData {
  paymentMethod: PaymentMethod;
  pixData?: PixQrCodeData;
}

interface PaymentPromise {
  resolve: (data: PaymentData) => void;
  reject: (error: Error) => void;
}

interface UsePaymentMethodCollectionReturn {
  isModalOpen: boolean;
  planName: string;
  planValue: number;
  paymentData: PaymentData | null;
  isLoading: boolean;
  pixData: PixQrCodeData | null;
  openModal: (planName: string, planValue: number, planId: string, billingPeriod: string) => Promise<PaymentData>;
  closeModal: () => void;
  handlePaymentMethodSelect: (paymentMethod: PaymentMethod) => void;
  generatePixQrCode: () => Promise<void>;
}

export const usePaymentMethodCollection = (): UsePaymentMethodCollectionReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planValue, setPlanValue] = useState(0);
  const [planId, setPlanId] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('');
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentPromise, setPaymentPromise] = useState<PaymentPromise | null>(null);
  const [pixData, setPixData] = useState<PixQrCodeData | null>(null);
  const { user } = useAuth();

  const openModal = (planName: string, planValue: number, planId: string, billingPeriod: string): Promise<PaymentData> => {
    return new Promise((resolve, reject) => {
      setPlanName(planName);
      setPlanValue(planValue);
      setPlanId(planId);
      setBillingPeriod(billingPeriod);
      setIsModalOpen(true);
      setPaymentPromise({ resolve, reject });
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPlanName('');
    setPlanValue(0);
    setPlanId('');
    setBillingPeriod('');
    setPaymentData(null);
    setIsLoading(false);
    setPixData(null);
    
    // Reject the promise if modal is closed without submitting
    if (paymentPromise) {
      paymentPromise.reject(new Error('Payment method collection cancelled'));
      setPaymentPromise(null);
    }
  };

  const handlePaymentMethodSelect = (selectedPaymentMethod: PaymentMethod) => {
    const data = { paymentMethod: selectedPaymentMethod, pixData: pixData || undefined };
    setPaymentData(data);
    
    // For CREDIT_CARD, just set the method - user will need to wait
    // For PIX, QR code generation will be handled separately
    if (selectedPaymentMethod === 'CREDIT_CARD') {
      // Don't resolve yet - this is coming soon
      return;
    }
  };

  const generatePixQrCode = async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-asaas-pix-qrcode', {
        body: {
          planName,
          planValue,
          userId: user.id,
          planId,
          billingPeriod
        }
      });

      if (error) throw error;

      // Log Asaas environment info
      if (data?.asaasEnvironment) {
        debugLogger.debug('asaas', 'Environment info:', {
          environment: data.asaasEnvironment.environment,
          baseUrl: data.asaasEnvironment.baseUrl,
          function: 'create-asaas-pix-qrcode'
        });
      }

      if (data?.success && data?.data) {
        // Detecta ambiente de teste (dev/preview/sandbox)
        const isTestMode = data.environment === 'development';
        
        // Log no console para dev/preview (requisito do usuário)
        if (isTestMode) {
          console.log('[ASAAS] ⚠️ PIX gerado em ambiente de DESENVOLVIMENTO/SANDBOX');
          console.log('[ASAAS] Este pagamento NÃO será cobrado - use para testes apenas');
        }
        
        const pixDataWithEnv = {
          ...data.data,
          isTestMode
        };
        
        setPixData(pixDataWithEnv);
        
        const paymentResult = { 
          paymentMethod: 'PIX' as PaymentMethod, 
          pixData: pixDataWithEnv 
        };
        setPaymentData(paymentResult);
        
        // Resolve the promise with PIX data
        if (paymentPromise) {
          paymentPromise.resolve(paymentResult);
          setPaymentPromise(null);
        }
      } else {
        throw new Error('Failed to generate PIX QR code');
      }
    } catch (error) {
      debugLogger.error('asaas', 'Error generating PIX QR code:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isModalOpen,
    planName,
    planValue,
    paymentData,
    isLoading,
    pixData,
    openModal,
    closeModal,
    handlePaymentMethodSelect,
    generatePixQrCode
  };
};
